// ───────────────────────────────────────────────────────────────────────────
// Demo core: tiny request router
// Each app declares its fake API as a flat list of [method, '/path/:param',
// handler] tuples. resolveWith() is what an app's makeRequest calls in demo mode:
// it matches the request, runs the handler and resolves after a small latency.
// Unknown endpoints fall back to safe, non-crashing empty responses.
// ───────────────────────────────────────────────────────────────────────────

export interface DemoRequest {
  url: string;
  method?: string;
  data?: unknown;
  params?: Record<string, unknown>;
  headers?: Record<string, unknown>;
}

export interface DemoCtx {
  /** Clean path: no scheme/host/query/trailing slash, leading slash kept. */
  path: string;
  /** Lower-case HTTP method. */
  method: string;
  /** ':param' values from the pattern, URI-decoded. */
  params: Record<string, string>;
  /** URL query string merged with request params (params win). */
  query: Record<string, unknown>;
  /** Request body (`data`). */
  body: unknown;
  /** X-Organization-ID header ?? query.organization_id ?? selected org in session. */
  orgId: string | null;
}

export type DemoHandler = (ctx: DemoCtx) => unknown | Promise<unknown>;

export type DemoRouteDef = [method: string, pattern: string, handler: DemoHandler];

export interface DemoRoute {
  method: string;
  pattern: string;
  re: RegExp;
  keys: string[];
  handler: DemoHandler;
}

export interface DemoMatch {
  route: DemoRoute;
  params: Record<string, string>;
}

export interface DemoResolveOptions {
  /** Org id when neither the header nor the query names one. Default: sessionStorage selectedOrganization.id. */
  getOrgId?: () => string | null;
  /** Called for every request no route matches. Default: console.warn in DEV only. */
  onUnhandled?: (method: string, path: string, query: Record<string, unknown>) => void;
  /** Override DEV detection for the default unhandled warning. Default: import.meta.env.DEV. */
  isDev?: boolean;
}

/** Shape of a demoError() rejection: enough of an AxiosError for the apps' error handling. */
export interface DemoAxiosError extends Error {
  isAxiosError: true;
  code: string;
  status: number;
  response: {
    status: number;
    statusText: string;
    headers: Record<string, string>;
    data: {
      detail: string;
      error: { message: string };
      response: { message: string };
    };
  };
}

export const DEMO_LATENCY_MS = 120;

let latencyMs = DEMO_LATENCY_MS;

/** Change the simulated latency (e.g. 0 in tests). */
export function setDemoLatency(ms: number): void {
  latencyMs = Math.max(0, ms);
}

export function getDemoLatency(): number {
  return latencyMs;
}

function snapshot<T>(value: T): T {
  // Hand the UI a copy so it can never mutate the in-memory demo store by accident.
  if (value === null || typeof value !== 'object') return value;
  try {
    return typeof structuredClone === 'function' ? structuredClone(value) : value;
  } catch {
    return value;
  }
}

/** Resolve with (a structured clone of) `v` after the demo latency. */
export function respond<T>(v: T): Promise<T> {
  const value = snapshot(v);
  return new Promise((resolve) => setTimeout(() => resolve(value), latencyMs));
}

function statusText(status: number): string {
  const map: Record<number, string> = {
    400: 'Bad Request',
    401: 'Unauthorized',
    403: 'Forbidden',
    404: 'Not Found',
    409: 'Conflict',
    422: 'Unprocessable Entity',
    500: 'Internal Server Error',
  };
  return map[status] ?? 'Error';
}

/** Reject (after the demo latency) with an axios-shaped error carrying `detail` everywhere apps look. */
export function demoError(status: number, detail: string): Promise<never> {
  const err = new Error(detail) as DemoAxiosError;
  err.name = 'AxiosError';
  err.isAxiosError = true;
  err.code = status >= 500 ? 'ERR_BAD_RESPONSE' : 'ERR_BAD_REQUEST';
  err.status = status;
  err.response = {
    status,
    statusText: statusText(status),
    headers: {},
    data: { detail, error: { message: detail }, response: { message: detail } },
  };
  return new Promise<never>((_, reject) => setTimeout(() => reject(err), latencyMs));
}

/** Strip scheme+host, query and hash, collapse '//' and drop the trailing slash; always keeps a leading slash. */
export function cleanPath(raw: string): string {
  let path = raw ?? '';
  // Only a leading 'scheme://host' counts (a '://' inside the query string must not).
  const origin = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\/[^/?#]*/.exec(path);
  if (origin) path = path.slice(origin[0].length);
  path = path.split('#')[0].split('?')[0];
  path = path.replace(/\/{2,}/g, '/');
  if (!path.startsWith('/')) path = `/${path}`;
  if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);
  return path;
}

const PARAM_RE = /:([A-Za-z_][A-Za-z0-9_]*)/g;

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function compile(pattern: string): { re: RegExp; keys: string[] } {
  const keys: string[] = [];
  let source = '';
  let last = 0;
  for (const m of pattern.matchAll(PARAM_RE)) {
    const index = m.index ?? 0;
    source += escapeRegex(pattern.slice(last, index)) + '([^/]+)';
    keys.push(m[1]);
    last = index + m[0].length;
  }
  source += escapeRegex(pattern.slice(last));
  return { re: new RegExp(`^${source}$`), keys };
}

/**
 * Compile route tuples. ':param' segments match one path segment; everything
 * else is literal. Method '*' matches any method. Order matters: first match
 * wins, so list '/pickup_order/incoming' before '/pickup_order/:id'.
 */
export function defineRoutes(defs: DemoRouteDef[]): DemoRoute[] {
  return defs.map(([method, rawPattern, handler]) => {
    const pattern = cleanPath(rawPattern);
    const { re, keys } = compile(pattern);
    return { method: method.toLowerCase(), pattern, re, keys, handler };
  });
}

function safeDecode(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

export function matchRoute(routes: DemoRoute[], method: string, path: string): DemoMatch | null {
  const m = (method || 'get').toLowerCase();
  const p = cleanPath(path);
  for (const route of routes) {
    if (route.method !== m && route.method !== '*') continue;
    const hit = route.re.exec(p);
    if (!hit) continue;
    const params: Record<string, string> = {};
    route.keys.forEach((key, i) => {
      params[key] = safeDecode(hit[i + 1]);
    });
    return { route, params };
  }
  return null;
}

export function matchesKnownRoute(routes: DemoRoute[], method: string, path: string): boolean {
  return matchRoute(routes, method, path) !== null;
}

function queryFromUrl(url: string): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const q = (url ?? '').split('#')[0];
  const idx = q.indexOf('?');
  if (idx === -1) return out;
  new URLSearchParams(q.slice(idx + 1)).forEach((value, key) => {
    const prev = out[key];
    if (prev === undefined) out[key] = value;
    else out[key] = Array.isArray(prev) ? [...prev, value] : [prev, value];
  });
  return out;
}

function paramsObject(params: unknown): Record<string, unknown> {
  if (!params || typeof params !== 'object') return {};
  if (typeof URLSearchParams !== 'undefined' && params instanceof URLSearchParams) {
    return queryFromUrl(`?${params.toString()}`);
  }
  return params as Record<string, unknown>;
}

function headerValue(headers: Record<string, unknown> | undefined, name: string): unknown {
  if (!headers || typeof headers !== 'object') return undefined;
  const wanted = name.toLowerCase();
  for (const key of Object.keys(headers)) {
    if (key.toLowerCase() === wanted) return headers[key];
  }
  return undefined;
}

function asId(value: unknown): string | null {
  if (Array.isArray(value)) return asId(value[0]);
  if (value === undefined || value === null) return null;
  const s = String(value).trim();
  return s === '' ? null : s;
}

function sessionOrgId(): string | null {
  try {
    const raw = typeof globalThis.sessionStorage === 'undefined' ? null : globalThis.sessionStorage.getItem('selectedOrganization');
    if (!raw) return null;
    return asId((JSON.parse(raw) as { id?: unknown } | null)?.id);
  } catch {
    return null;
  }
}

function detectDev(): boolean {
  try {
    if (typeof import.meta === 'undefined') return false;
    return Boolean((import.meta as unknown as { env?: { DEV?: unknown } }).env?.DEV);
  } catch {
    return false;
  }
}

export const DEMO_FALLBACK_GET = { result: null, result_list: [] as unknown[], total_count: 0 };
export const DEMO_FALLBACK_WRITE = { result: { success: true } };

/**
 * Answer a request from `routes`. First matching route wins; its result goes
 * through respond(). Handler rejections (including demoError) propagate as-is.
 * Unmatched requests call onUnhandled and resolve with a safe empty fallback.
 */
export function resolveWith(routes: DemoRoute[], req: DemoRequest, opts: DemoResolveOptions = {}): Promise<unknown> {
  const method = (req.method || 'get').toLowerCase();
  const path = cleanPath(req.url || '');
  const extra = paramsObject(req.params);
  const query: Record<string, unknown> = { ...queryFromUrl(req.url || ''), ...extra };
  const hit = matchRoute(routes, method, path);

  if (!hit) {
    if (opts.onUnhandled) {
      opts.onUnhandled(method, path, query);
    } else if (opts.isDev ?? detectDev()) {
      console.warn('[demo] unhandled endpoint', method.toUpperCase(), path, query);
    }
    // respond() clones, so the shared fallback objects are never handed out by reference.
    return respond(method === 'get' ? DEMO_FALLBACK_GET : DEMO_FALLBACK_WRITE);
  }

  const orgId =
    asId(headerValue(req.headers, 'X-Organization-ID')) ??
    asId(query.organization_id) ??
    (opts.getOrgId ? opts.getOrgId() : sessionOrgId());

  const ctx: DemoCtx = { path, method, params: hit.params, query, body: req.data, orgId };

  let out: unknown;
  try {
    out = hit.route.handler(ctx);
  } catch (error) {
    return Promise.reject(error);
  }
  return Promise.resolve(out).then((value) => respond(value));
}

/** A query value as a string ('' and null/undefined -> undefined; arrays -> first item). */
export function parseQueryParam(ctx: Pick<DemoCtx, 'query'>, key: string): string | undefined {
  let v = ctx.query[key];
  if (Array.isArray(v)) v = v[0];
  if (v === undefined || v === null) return undefined;
  const s = String(v);
  return s === '' ? undefined : s;
}

function positiveInt(value: unknown): number | null {
  const n = typeof value === 'number' ? value : Number.parseInt(String(value ?? ''), 10);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
}

/**
 * 1-based page slice. Missing/invalid page -> 1. Missing/invalid limit -> all
 * items on one page (limit reported as max(total, 1)).
 */
export function paginate<T>(items: T[], page?: unknown, limit?: unknown): { items: T[]; total: number; page: number; limit: number } {
  const total = items.length;
  const size = positiveInt(limit);
  if (size === null) return { items: items.slice(), total, page: 1, limit: Math.max(total, 1) };
  const p = positiveInt(page) ?? 1;
  const start = (p - 1) * size;
  return { items: items.slice(start, start + size), total, page: p, limit: size };
}
